import React from 'react'
import {Col, Form} from 'react-bootstrap'
import {useLocation} from 'react-router-dom'

import api from '../../../api'
import {fetchSectionList, fetchSectionMeta} from './helpers/siteApi'
import BladeBlockPreview from './src/BladeBlockPreview'
import GenericSectionEditor, {SectionMeta} from './src/GenericSectionEditor'

type Block = {
    id?: number
    page_id: number
    type: string
    position: number
    locale: string
    version: 'draft' | 'published'
    value: any
    __uid?: string
}

type Page = {
    id: number
    url: string
    title?: string
    locale: string
    status: 'draft' | 'published'
}

export type TModuleSiteStudio = {
    type: 'site_studio'
    size: number
    settings: {
        object_page: string
        object_blocks: string
        locale?: string
        version?: 'draft' | 'published'
        available_block_types?: string[]
    }
}

function randomKey(): string {
    return Math.random().toString(36).slice(2) + Date.now().toString(36)
}
function normalizeType(t: any): string {
    if (typeof t === 'string') return t
    if (t && typeof t === 'object') return t.value ?? t.key ?? t.id ?? ''
    return ''
}
function normalizeBlock(b: any): Block {
    return {
        ...b,
        type: normalizeType(b?.type),
        __uid: b.__uid || randomKey(),
    }
}

const ContentEditor: React.FC<{
    value: any
    onChange: (next: any) => void
    selected?: boolean
    onSelect?: () => void
}> = (props) => {
    const current = props.value || {}
    return (
        <div
            onClick={props.onSelect}
            style={{
                border: props.selected ? '2px solid #4f46e5' : '1px dashed #cbd5e1',
                padding: 16,
                marginBottom: 12,
                background: '#fff',
                position: 'relative',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: 6,
                    right: 8,
                    fontSize: 12,
                    background: '#f1f5f9',
                    padding: '2px 6px',
                    borderRadius: 6,
                }}
            >
                content
            </div>

            <input
                value={current.sectionTitle || ''}
                placeholder='Заголовок'
                onChange={(e) => props.onChange({...current, sectionTitle: e.target.value})}
                style={{
                    display: 'block',
                    width: '100%',
                    fontSize: 20,
                    fontWeight: 600,
                    marginBottom: 8,
                }}
            />

            <textarea
                rows={6}
                value={current.html || ''}
                placeholder='HTML/текст'
                onChange={(e) => props.onChange({...current, html: e.target.value})}
                style={{width: '100%', fontFamily: 'inherit'}}
            />
        </div>
    )
}

const EDITOR_REGISTRY: Record<string, React.FC<any>> = {
    content: ContentEditor,
}

type EditorCardProps = {
    block: Block
    index: number
    total: number
    ensureMeta: (key: string) => Promise<SectionMeta>
    setBlockValue: (index: number, nextValue: any) => void
    moveBlock: (from: number, to: number) => void
    removeBlock: (index: number) => void
}

const EditorCard: React.FC<EditorCardProps> = React.memo(
    ({block, index, total, ensureMeta, setBlockValue, moveBlock, removeBlock}) => {
        const [localMeta, setLocalMeta] = React.useState<SectionMeta | null>(null)
        const [isLoadingMeta, setIsLoadingMeta] = React.useState<boolean>(false)
        const [showPreview, setShowPreview] = React.useState(false)
        const [previewTick, setPreviewTick] = React.useState(0)

        React.useEffect(() => {
            let cancelled = false
            ;(async () => {
                setIsLoadingMeta(true)
                try {
                    const meta = await ensureMeta(block.type)
                    if (!cancelled) setLocalMeta(meta)
                } catch {
                    if (!cancelled) setLocalMeta({title: block.type, fields: []})
                } finally {
                    if (!cancelled) setIsLoadingMeta(false)
                }
            })()
            return () => {
                cancelled = true
            }
        }, [block.type, ensureMeta])

        const CustomEditor = EDITOR_REGISTRY[block.type]

        return (
            <div style={{marginBottom: 24}}>
                {/* Панель действий */}
                <div style={{display: 'flex', gap: 8, margin: '6px 0 12px'}}>
                    <button
                        className='btn btn-sm btn-light'
                        onClick={() => moveBlock(index, Math.max(0, index - 1))}
                    >
                        ↑
                    </button>
                    <button
                        className='btn btn-sm btn-light'
                        onClick={() => moveBlock(index, Math.min(total - 1, index + 1))}
                    >
                        ↓
                    </button>
                    <button className='btn btn-sm btn-danger' onClick={() => removeBlock(index)}>
                        Удалить
                    </button>
                    <button
                        className='btn btn-sm btn-outline-primary'
                        onClick={() => {
                            setShowPreview(true)
                            setPreviewTick((t) => t + 1)
                        }}
                    >
                        Показать превью
                    </button>
                </div>

                {/* Редактор */}
                <div style={{marginBottom: 12}}>
                    {isLoadingMeta && <div className='text-muted'>Загрузка схемы…</div>}
                    {!isLoadingMeta &&
                        (CustomEditor ? (
                            <CustomEditor
                                value={block.value || {}}
                                onChange={(v: any) => setBlockValue(index, v)}
                                selected={false}
                                onSelect={() => {}}
                            />
                        ) : localMeta ? (
                            <GenericSectionEditor
                                meta={localMeta}
                                value={block.value || {}}
                                onChange={(v) => setBlockValue(index, v)}
                            />
                        ) : (
                            <div className='text-muted'>Схема не найдена</div>
                        ))}
                </div>

                {/* Превью */}
                {showPreview && (
                    <div>
                        <BladeBlockPreview
                            keyName={block.type}
                            value={block.value}
                            inlineCss={false}
                            refreshKey={previewTick}
                        />
                    </div>
                )}
            </div>
        )
    }
)

const ModuleSiteStudio: React.FC<TModuleSiteStudio> = ({settings}) => {
    const location = useLocation()

    const pageId = React.useMemo(() => {
        const parts = location.pathname.split('/')
        const last = parts[parts.length - 1]
        return Number.isFinite(+last) ? +last : null
    }, [location.pathname])

    const [page, setPage] = React.useState<Page | null>(null)
    const [blocks, setBlocks] = React.useState<Block[]>([])
    const [saveState, setSaveState] = React.useState<'idle' | 'saving' | 'saved'>('idle')
    const [isLoading, setIsLoading] = React.useState<boolean>(true)
    const [availableSections, setAvailableSections] = React.useState<
        Array<{key: string; title: string}>
    >([])

    const [metaCache, setMetaCache] = React.useState<Record<string, SectionMeta>>({})
    const [metaLoadingMap, setMetaLoadingMap] = React.useState<Record<string, boolean>>({})

    const locale = settings.locale || 'ru'
    const version = settings.version || 'draft'

    // список секций
    React.useEffect(() => {
        ;(async () => {
            try {
                const list = await fetchSectionList()
                const normalized = list.map((it) => ({
                    key: it.key,
                    title: it.title?.trim() || it.key,
                }))
                setAvailableSections(normalized)
            } catch {
                const fallback = (settings.available_block_types || []).map((key) => ({
                    key,
                    title: key,
                }))
                setAvailableSections(fallback)
            }
        })()
    }, [settings.available_block_types])

    // загрузка страницы и блоков
    React.useEffect(() => {
        if (!pageId) return
        setIsLoading(true)
        ;(async () => {
            try {
                const pageResponse = await api<any>(settings.object_page, 'get', {
                    id: pageId,
                    withBlocks: false,
                })
                const pageData = Array.isArray(pageResponse?.data)
                    ? pageResponse.data[0]
                    : pageResponse?.data
                setPage(pageData || null)

                const blocksResponse = await api<any>(settings.object_blocks, 'get', {
                    page_id: pageId,
                    version: 'draft',
                    locale,
                    ORDER: {position: 'ASC', id: 'ASC'},
                })

                const raw = Array.isArray(blocksResponse?.data) ? blocksResponse.data : []
                setBlocks(raw.map(normalizeBlock))
            } finally {
                setIsLoading(false)
            }
        })()
    }, [pageId, locale, settings.object_blocks, settings.object_page])

    const ensureMeta = React.useCallback(
        async (key: string): Promise<SectionMeta> => {
            if (metaCache[key]) return metaCache[key]
            setMetaLoadingMap((m) => ({...m, [key]: true}))
            try {
                const meta = await fetchSectionMeta(key)
                setMetaCache((prev) => ({...prev, [key]: meta}))
                return meta
            } finally {
                setMetaLoadingMap((m) => ({...m, [key]: false}))
            }
        },
        [metaCache]
    )

    const moveBlock = React.useCallback((from: number, to: number) => {
        setBlocks((prev) => {
            const next = [...prev]
            const [item] = next.splice(from, 1)
            next.splice(to, 0, item)
            return next
        })
    }, [])

    const removeBlock = React.useCallback((index: number) => {
        setBlocks((prev) => prev.filter((_, i) => i !== index))
    }, [])

    const setBlockValue = React.useCallback((index: number, nextValue: any) => {
        setBlocks((prev) => {
            const next = [...prev]
            next[index] = {...next[index], value: nextValue}
            return next
        })
    }, [])

    const saveBlocks = async () => {
        if (!page?.id) return
        try {
            setSaveState('saving')
            const payload = blocks.map((block, index) => ({
                id: block.id,
                type: normalizeType(block.type),
                position: index * 10 + 10,
                value: block.value,
                locale,
                version,
            }))

            await api(settings.object_blocks, 'bulk_upsert', {
                page_id: page.id,
                blocks: payload,
                locale,
                version,
                replace: true,
            })

            setSaveState('saved')
            setTimeout(() => setSaveState('idle'), 1000)
        } catch (err) {
            console.error(err)
            setSaveState('idle')
        }
    }

    const addBlock = async (type: string) => {
        if (!page?.id) return
        let defaults: any = {}
        try {
            const meta = await ensureMeta(type)
            // собрать дефолты из меты
            const out: any = {}
            for (const field of meta.fields || []) {
                const f: any = field as any
                if (f.type === 'repeater') {
                    out[f.name] = Array.isArray(f.default) ? f.default : []
                } else {
                    out[f.name] = f.default ?? (f.type === 'checkbox' ? false : '')
                }
            }
            defaults = out
        } catch {
            defaults = {}
        }

        setBlocks((prev) => {
            const lastPosition = prev.length ? prev[prev.length - 1].position : 0
            return [
                ...prev,
                {
                    __uid: randomKey(),
                    page_id: page.id,
                    type,
                    position: lastPosition + 10,
                    value: defaults,
                    locale,
                    version,
                },
            ]
        })
    }

    const publish = async () => {
        if (!page?.id) return
        await api(settings.object_page, 'publish', {page_id: page.id, locale})
    }

    return (
        <Form.Group as={Col}>
            <div className='card'>
                <div className='card-header d-flex justify-content-between align-items-center'>
                    <div>
                        <h3 className='card-title mb-0'>
                            {page?.title || page?.url || 'Страница'}{' '}
                            <span className='badge bg-light text-dark ms-2'>draft</span>
                        </h3>
                        <div style={{fontSize: 12, color: '#64748b'}}>
                            {isLoading
                                ? 'Загрузка…'
                                : saveState === 'saving'
                                ? 'Сохранение…'
                                : saveState === 'saved'
                                ? 'Сохранено'
                                : ''}
                        </div>
                    </div>

                    <div className='d-flex gap-2'>
                        <select
                            className='form-select form-select-sm'
                            defaultValue=''
                            disabled={!page || isLoading}
                            onChange={(e) => {
                                const type = e.target.value
                                if (type) addBlock(type)
                                e.currentTarget.value = ''
                            }}
                        >
                            <option value='' disabled>
                                Добавить блок…
                            </option>
                            {availableSections.map((item) => (
                                <option key={item.key} value={item.key}>
                                    {item.title}
                                </option>
                            ))}
                        </select>

                        <button
                            className='btn btn-sm btn-success'
                            onClick={saveBlocks}
                            disabled={!page || isLoading}
                        >
                            Сохранить
                        </button>
                        <button
                            className='btn btn-sm btn-primary'
                            onClick={publish}
                            disabled={!page || isLoading}
                        >
                            Опубликовать
                        </button>
                    </div>
                </div>

                <div className='card-body'>
                    {blocks.map((block, index) => (
                        <EditorCard
                            key={block.__uid || `${block.id ?? 'new'}:${index}:${block.type}`}
                            block={block}
                            index={index}
                            total={blocks.length}
                            ensureMeta={ensureMeta}
                            setBlockValue={setBlockValue}
                            moveBlock={moveBlock}
                            removeBlock={removeBlock}
                        />
                    ))}
                    {!blocks.length && !isLoading && (
                        <div className='text-muted'>
                            Нет блоков. Добавь первый через селект сверху.
                        </div>
                    )}
                </div>
            </div>
        </Form.Group>
    )
}

export default ModuleSiteStudio
